import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CBadge,
  CCallout,
  CFormLabel,
  CFormSelect,
  CProgress,
  CSmartTable,
} from "@coreui/react-pro";
import EmployerPageFrame from "../../components/employer/EmployerPageFrame";
import useEmployerOsWorkspace from "../../hooks/useEmployerOsWorkspace";
import {
  buildAnalyticsSnapshot,
  buildCandidateCaseRows,
  buildDocumentRows,
  formatDate,
} from "../../services/employer/employerCaseOsDomain";
import { formatEmployerAdFocusLabel } from "../../utils/employerAdLabels";
import {
  buildEmployerTrustIncidentRows,
  buildEmployerTrustOpsSummary,
  buildEmployerTrustSummary,
  formatEmployerTrustFlagLabel,
  getEmployerTrustStatusColor,
  getPublishingRightsStageColor,
  getRiskLevelColor,
  getTrustTierColor,
} from "../../services/employer/employerTrustDomain";

export default function EmployerAnalyticsPage() {
  const [searchParams] = useSearchParams();
  const workspace = useEmployerOsWorkspace();
  const requestedAdId = searchParams.get("adId") || "";
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState("");
  const [analyticsApplications, setAnalyticsApplications] = useState([]);
  const [analyticsDetailsById, setAnalyticsDetailsById] = useState({});
  const [analyticsFilingById, setAnalyticsFilingById] = useState({});

  useEffect(() => {
    if (requestedAdId) workspace.setSelectedAdId(requestedAdId);
  }, [requestedAdId, workspace.setSelectedAdId]);

  const loadAnalyticsSnapshot = useCallback(async () => {
    const allAds = Array.isArray(workspace.ads) ? workspace.ads.filter(Boolean) : [];
    if (!allAds.length) {
      setAnalyticsApplications([]);
      setAnalyticsDetailsById({});
      setAnalyticsFilingById({});
      setAnalyticsError("");
      return;
    }

    try {
      setAnalyticsLoading(true);
      const applicationBuckets = await Promise.all(
        allAds.map(async (ad) => {
          try {
            const result = await workspace.adapter.loadApplications({ adId: ad.id });
            const rows = Array.isArray(result?.rows) ? result.rows.filter(Boolean) : [];
            return rows.map((row) => ({
              ...row,
              ad_id: row.ad_id || row.anzeigen_id || ad.id,
              anzeigen_id: row.anzeigen_id || row.ad_id || ad.id,
            }));
          } catch (_error) {
            return [];
          }
        })
      );

      const nextApplications = Array.from(
        new Map(applicationBuckets.flat().map((row) => [String(row.id), row])).values()
      );

      const [detailsEntries, filingEntries] = await Promise.all([
        Promise.all(
          nextApplications.map(async (row) => {
            try {
              const detail = await workspace.adapter.loadDecisionSummary({ applicationId: row.id });
              return [String(row.id), detail || null];
            } catch (_error) {
              return [String(row.id), null];
            }
          })
        ),
        Promise.all(
          nextApplications.map(async (row) => {
            try {
              const filing = await workspace.adapter.loadAuthorityFilingKit({ applicationId: row.id });
              return [String(row.id), filing?.kit || filing || null];
            } catch (_error) {
              return [String(row.id), null];
            }
          })
        ),
      ]);

      setAnalyticsApplications(nextApplications);
      setAnalyticsDetailsById(Object.fromEntries(detailsEntries));
      setAnalyticsFilingById(Object.fromEntries(filingEntries));
      setAnalyticsError("");
    } catch (nextError) {
      setAnalyticsApplications([]);
      setAnalyticsDetailsById({});
      setAnalyticsFilingById({});
      setAnalyticsError(nextError?.response?.data?.message || "Analytics-Snapshot konnte nicht geladen werden.");
    } finally {
      setAnalyticsLoading(false);
    }
  }, [workspace.adapter, workspace.ads]);

  useEffect(() => {
    loadAnalyticsSnapshot();
  }, [loadAnalyticsSnapshot]);

  const caseRows = useMemo(
    () => buildCandidateCaseRows({
      ads: workspace.ads,
      applications: analyticsApplications,
      detailsById: analyticsDetailsById,
      filingById: analyticsFilingById,
    }),
    [analyticsApplications, analyticsDetailsById, analyticsFilingById, workspace.ads]
  );
  const documentRows = useMemo(() => buildDocumentRows({ caseRows }), [caseRows]);
  const analytics = useMemo(
    () => buildAnalyticsSnapshot({
      ads: workspace.ads,
      caseRows,
      documentRows,
      profile: workspace.profile || {},
    }),
    [caseRows, documentRows, workspace.ads, workspace.profile]
  );
  const trustSummary = useMemo(() => buildEmployerTrustSummary(workspace.profile || {}), [workspace.profile]);
  const trustOpsSummary = useMemo(() => buildEmployerTrustOpsSummary(workspace.profile || {}), [workspace.profile]);
  const trustIncidentRows = useMemo(() => buildEmployerTrustIncidentRows(workspace.profile || {}), [workspace.profile]);

  const summaryCards = [
    {
      label: "Aktive Anzeigen",
      value: analytics.activeAds,
      note: `${analytics.totalAds} Anzeigen im Employer OS gelistet.`,
      progress: analytics.totalAds ? Math.round((analytics.activeAds / analytics.totalAds) * 100) : 0,
    },
    {
      label: "Filing-ready Cases",
      value: analytics.filingReadyCases,
      note: `${analytics.totalCases} Cases im aktuellen Datenschnitt.`,
      progress: analytics.totalCases ? Math.round((analytics.filingReadyCases / analytics.totalCases) * 100) : 0,
    },
    {
      label: "Dokumentenrisiken",
      value: analytics.documentIssues,
      note: `${analytics.visibleOriginalDocuments} Originale sind mit sichtbarer Freigabe verfuegbar.`,
      progress: analytics.visibleOriginalDocuments + analytics.documentIssues
        ? Math.round((analytics.documentIssues / (analytics.visibleOriginalDocuments + analytics.documentIssues)) * 100)
        : 0,
    },
    {
      label: "Score stark/stabil",
      value: `${analytics.scoreDistribution.strong}/${analytics.scoreDistribution.stable}`,
      note: `${analytics.scoreDistribution.fragile + analytics.scoreDistribution.critical} Cases sind fragil oder kritisch.`,
      progress: analytics.totalCases
        ? Math.round(((analytics.scoreDistribution.strong + analytics.scoreDistribution.stable) / analytics.totalCases) * 100)
        : 0,
    },
    {
      label: "Ø Zeit bis Entscheidung",
      value: analytics.averageDecisionDays ? `${analytics.averageDecisionDays.toFixed(1)} Tage` : "k.A.",
      note: "Abgeleitet aus erstellt/letzte sichtbare Entscheidungspflege.",
      progress: analytics.averageDecisionDays ? Math.min(100, Math.round(analytics.averageDecisionDays * 10)) : 0,
    },
    {
      label: "Employer Trust",
      value: trustSummary.trustStatusLabel,
      note: `${trustSummary.counts.verified} verifiziert / ${trustSummary.counts.unclear} unklar / ${trustSummary.counts.blocked} blockiert`,
      progress: Math.min(100, trustSummary.trustLevel * 25),
    },
    {
      label: "Trust Incidents",
      value: trustOpsSummary.activeIncidents,
      note: `${trustOpsSummary.criticalIncidents} kritisch · Workflow ${trustOpsSummary.reviewWorkflowLabel}`,
      progress: trustOpsSummary.activeIncidents ? Math.min(100, trustOpsSummary.activeIncidents * 20) : 0,
    },
    {
      label: "Trust Tier",
      value: trustOpsSummary.trustTierLabel,
      note: `${trustOpsSummary.employerClassificationLabel} · ${trustOpsSummary.publishingRightsStageLabel}`,
      progress: trustSummary.trustLevel ? Math.min(100, trustSummary.trustLevel * 20) : 0,
    },
    {
      label: "Risk Level",
      value: trustOpsSummary.riskLevelLabel,
      note: `GPT ${(Number(trustOpsSummary.realityCheck.riskScore || 0) * 100).toFixed(0)}% · Behavior ${(trustOpsSummary.behaviorRiskScore * 100).toFixed(0)}%`,
      progress: Math.min(100, Math.round(Math.max(Number(trustOpsSummary.realityCheck.riskScore || 0), trustOpsSummary.behaviorRiskScore || 0) * 100)),
    },
  ];

  const statusRows = Object.entries(analytics.statusCounts || {}).map(([status, count]) => ({
    id: status,
    status,
    count,
  }));

  return (
    <EmployerPageFrame
      title="Analytics"
      subtitle="Analytics werden handlungsleitend gerechnet: Funnel je Anzeige, Case-Status, Dokumentenrisiken und Entscheidungsgeschwindigkeit aus derselben Betriebsschicht."
      contentClassName="space-y-4"
      shellProps={{
        shellLabel: "Employer Analytics OS",
        shellDescription: "Analytics entstehen aus denselben Jobs, Cases, Dokumenten und Filing-Signalen, nicht aus dekorativen KPI-Kacheln.",
        topContext: "Analytics",
      }}
    >
      {workspace.error || analyticsError ? (
        <CCallout color="danger">
          <strong>Analytics-Snapshot konnte nicht vollstaendig geladen werden.</strong>
          <div>{analyticsError || workspace.error}</div>
        </CCallout>
      ) : null}

      <section className="dashboard-card-shell space-y-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <span className="dashboard-kicker">Snapshot</span>
            <h2 className="dashboard-card-title">Operative Analytics statt Dekoration</h2>
            <p className="dashboard-section-copy">Die Kennzahlen bleiben in der Sprache echter Entscheidungen: Welche Anzeige zieht, welche Cases blockieren, welche Dokumente bremsen und wie schnell Entscheidungen wirklich vorankommen.</p>
          </div>
          <div className="min-w-[260px]">
            <CFormLabel>Anzeige</CFormLabel>
            <CFormSelect
              value={workspace.selectedAdId}
              onChange={(event) => workspace.setSelectedAdId(event.target.value)}
              options={(workspace.ads || []).map((entry) => ({
                label: formatEmployerAdFocusLabel(entry),
                value: String(entry.id),
              }))}
            />
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {summaryCards.map((item) => (
            <div key={item.label} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <span className="dashboard-summary-label">{item.label}</span>
              <strong>{item.value}</strong>
              <p className="mb-2 text-sm text-slate-600">{item.note}</p>
              <CProgress value={item.progress} />
            </div>
          ))}
        </div>

        <div className="grid gap-4 xl:grid-cols-2">
          <div className="rounded-lg border border-slate-200 bg-white p-3">
            <CCallout color={analytics.blockerCases || trustSummary.flags.length ? "warning" : "success"}>
              <strong>{analytics.blockerCases || trustSummary.flags.length ? "Compliance- und Trust-Risiken sichtbar" : "Keine harten Case-Blocker im Fokus"}</strong>
              <div>
                {analytics.blockerCases || trustSummary.flags.length
                  ? `${analytics.blockerCases} Cases blockieren aktuell die naechste produktive Entscheidung. ${trustSummary.flags.length} Trust-Signale liegen auf dem Employer.`
                  : "Der aktuelle Fokus zeigt keine blockernden Filing- oder Trust-Signale."}
              </div>
            </CCallout>
            <p className="mb-0 text-sm text-slate-600">
              Snapshotdatum: {formatDate(new Date())} · Profilverankerung: {analytics.gateSummary.statusLabel} · Trust: {trustSummary.trustStatusLabel}{analyticsLoading ? " · Analytics werden aktualisiert" : ""}
            </p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-3">
            <span className="dashboard-summary-label">Statusverteilung</span>
            {statusRows.length ? (
              <CSmartTable
                columns={[
                  { key: "status", label: "Status" },
                  { key: "count", label: "Count" },
                ]}
                items={statusRows}
                pagination={false}
                sorter={false}
                tableProps={{ hover: true, responsive: true }}
              />
            ) : (
              <p className="mb-0 text-sm text-slate-600">Noch keine Cases fuer die Statusverteilung vorhanden.</p>
            )}
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="mb-3 d-flex justify-content-between gap-3">
            <span className="dashboard-summary-label">Employer-Risiko</span>
            <CCallout color={getEmployerTrustStatusColor(trustSummary.trustStatus)} className="mb-0 py-2 px-3">
              <strong>{trustSummary.trustStatusLabel}</strong>
            </CCallout>
          </div>
          <div className="mb-3 d-flex flex-wrap gap-2">
            <CBadge color={getTrustTierColor(trustOpsSummary.trustTier)}>{trustOpsSummary.trustTierLabel}</CBadge>
            <CBadge color={getRiskLevelColor(trustOpsSummary.riskLevel)}>{trustOpsSummary.riskLevelLabel}</CBadge>
            <CBadge color={getPublishingRightsStageColor(trustOpsSummary.publishingRightsStage)}>{trustOpsSummary.publishingRightsStageLabel}</CBadge>
            {trustOpsSummary.suspiciousActivityFlag ? <CBadge color="danger">Suspicious activity</CBadge> : null}
            {trustOpsSummary.realityCheck.requiresReview ? <CBadge color="warning">Reality Review</CBadge> : null}
          </div>
          {trustSummary.flags.length ? (
            <CSmartTable
              columns={[
                { key: "flag", label: "Signal" },
                { key: "label", label: "Bedeutung" },
              ]}
              items={trustSummary.flags.map((flag) => ({ id: flag, flag, label: formatEmployerTrustFlagLabel(flag) }))}
              pagination={false}
              sorter={false}
              tableProps={{ hover: true, responsive: true }}
            />
          ) : (
            <p className="mb-0 text-sm text-slate-600">Keine aktiven Fake-/Misrepresentation-Signale.</p>
          )}
          <p className="mb-0 mt-3 text-sm text-slate-600">
            Klassifikation: {trustOpsSummary.employerClassificationLabel} · GPT-Plausibilitaet {(Number(trustOpsSummary.realityCheck.plausibilityScore || 0) * 100).toFixed(0)}% · GPT-Risiko {(Number(trustOpsSummary.realityCheck.riskScore || 0) * 100).toFixed(0)}%
          </p>
          <p className="mb-0 mt-3 text-sm text-slate-600">
            Review: {trustOpsSummary.reviewWorkflowLabel} · Blocking Tasks: {trustOpsSummary.blockingTasks} · Investigation: {trustOpsSummary.underInvestigation ? "ja" : "nein"} · Risiko: {trustOpsSummary.riskLevel}
          </p>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <span className="dashboard-summary-label">GPT Reality Check</span>
          <CCallout color={trustOpsSummary.realityCheck.requiresReview || trustOpsSummary.riskLevel === "high" || trustOpsSummary.riskLevel === "critical" ? "warning" : "info"} className="mt-3">
            <strong>{trustOpsSummary.realityCheck.summary || "Noch kein externer Reality Check hinterlegt."}</strong>
            <div>{trustOpsSummary.realityCheck.recommendation || "Ohne externes Risiko-Signal bleibt die operative Trust-Pruefung intern fuehrend."}</div>
          </CCallout>
          {trustOpsSummary.realityCheck.flags?.length ? (
            <CSmartTable
              columns={[
                { key: "flag", label: "GPT-Signal" },
                { key: "label", label: "Bedeutung" },
              ]}
              items={trustOpsSummary.realityCheck.flags.map((flag) => ({ id: `reality-${flag}`, flag, label: formatEmployerTrustFlagLabel(flag) }))}
              pagination={false}
              sorter={false}
              tableProps={{ hover: true, responsive: true }}
            />
          ) : (
            <p className="mb-0 text-sm text-slate-600">Keine aktiven GPT-Risk-Signale.</p>
          )}
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <span className="dashboard-summary-label">Trust Incidents</span>
          {trustIncidentRows.length ? (
            <CSmartTable
              columns={[
                { key: "typeLabel", label: "Incident" },
                { key: "severity", label: "Severity" },
                { key: "statusLabel", label: "Status" },
                { key: "reportedBy", label: "Reported by" },
              ]}
              items={trustIncidentRows}
              pagination={false}
              sorter={false}
              tableProps={{ hover: true, responsive: true }}
            />
          ) : (
            <p className="mb-0 text-sm text-slate-600">Keine Trust-Incidents im aktuellen Employer-Profil.</p>
          )}
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <span className="dashboard-summary-label">Funnel je Anzeige</span>
          {analytics.funnelRows.length ? (
            <CSmartTable
              columns={[
                { key: "adTitle", label: "Anzeige" },
                { key: "adStatus", label: "Status" },
                { key: "totalApplications", label: "Bewerbungen" },
                { key: "reviewCount", label: "Review" },
                { key: "shortlistCount", label: "Shortlist" },
                { key: "blockedCount", label: "Blockierte Cases" },
                { key: "filingReadyCount", label: "Filing-ready" },
              ]}
              items={analytics.funnelRows}
              pagination={false}
              sorter={false}
              tableProps={{ hover: true, responsive: true }}
            />
          ) : (
            <p className="mb-0 text-sm text-slate-600">Noch keine Anzeigen fuer Funnel-Analytics vorhanden.</p>
          )}
        </div>
      </section>
    </EmployerPageFrame>
  );
}
